import type { OnVerifyMagicAuth } from '#lib/auth-handlers'

import { cn } from '@moment/design-system-ui/lib/cn'
import {
  type ClipboardEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Button, ButtonVariant } from '#components/controls/button'
import { ErrorText } from '#components/controls/input'
import { MagicAuthField } from '#components/controls/magic-auth-field'
import { Text } from '#components/controls/text'

import { lastFilledIndex } from './helpers'

const NON_DIGIT = /\D/g
const SINGLE_DIGIT = /^\d$/

// Stable keys for the fixed-length 6-digit field row.
const FIELD_KEYS = [
  'digit-0',
  'digit-1',
  'digit-2',
  'digit-3',
  'digit-4',
  'digit-5',
] as const

interface MagicAuthCodeProps {
  onVerifyMagicAuth: OnVerifyMagicAuth
  onSuccess: () => void // Called when verification succeeds
  onError?: (error: string) => void // Called when verification fails
  email: string // Email to verify
  disabled?: boolean
  onFocus?: () => void
}

export const MagicAuthCode = ({
  onVerifyMagicAuth,
  onSuccess,
  onError,
  email,
  disabled = false,
  onFocus,
}: MagicAuthCodeProps) => {
  const [code, setCode] = useState<string[]>(new Array(6).fill(''))
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleVerification = useCallback(
    async (verificationCode: string) => {
      if (verificationCode.length !== 6) {
        return
      }

      setIsVerifying(true)
      setError(null)

      try {
        const authResponse = await onVerifyMagicAuth({
          email,
          code: verificationCode,
        })

        if (authResponse.success) {
          onSuccess()
        } else {
          const errorMessage = authResponse.error || 'Verification failed'
          setError(errorMessage)
          onError?.(errorMessage)
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error'
        setError(errorMessage)
        onError?.(errorMessage)
      } finally {
        setIsVerifying(false)
      }
    },
    [email, onSuccess, onError, onVerifyMagicAuth],
  )

  // Keep a ref to the latest handleVerification so the auto-submit effect
  // doesn't depend on callback identity - only on the code changing.
  const handleVerificationRef = useRef(handleVerification)
  handleVerificationRef.current = handleVerification

  // Auto-submit when all digits are entered.
  useEffect(() => {
    const currentCode = code.join('')
    if (code.every((digit) => digit !== '') && currentCode.length === 6) {
      handleVerificationRef.current(currentCode)
    }
  }, [code])

  const handleChange = useCallback(
    (index: number, value: string) => {
      if (error) {
        setError(null)
      }

      if (value.length > 1) {
        // Handle paste
        const pastedCode = value.slice(0, 6).split('')
        const newCode = [...code]
        pastedCode.forEach((digit, i) => {
          if (index + i < 6 && SINGLE_DIGIT.test(digit)) {
            newCode[index + i] = digit
          }
        })
        setCode(newCode)
        const lastIndex = lastFilledIndex(newCode)
        const nextIndex = Math.min(lastIndex + 1, 5)
        inputRefs.current[nextIndex]?.focus()
      } else if (SINGLE_DIGIT.test(value)) {
        // Single digit input
        const newCode = [...code]
        newCode[index] = value
        setCode(newCode)
        if (index < 5) {
          inputRefs.current[index + 1]?.focus()
        }
      }
    },
    [code, error],
  )

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (error) {
          setError(null)
        }

        e.preventDefault()
        const newCode = [...code]
        if (code[index] !== '') {
          newCode[index] = ''
          setCode(newCode)
        } else if (index > 0) {
          newCode[index - 1] = ''
          setCode(newCode)
          inputRefs.current[index - 1]?.focus()
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault()
        inputRefs.current[index - 1]?.focus()
      } else if (e.key === 'ArrowRight' && index < 5) {
        e.preventDefault()
        inputRefs.current[index + 1]?.focus()
      }
    },
    [code, error],
  )

  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain')
    const cleanedData = pastedData.replace(NON_DIGIT, '').slice(0, 6)
    if (cleanedData) {
      const newCode = new Array(6).fill('').map((_, i) => cleanedData[i] || '')
      setCode(newCode)
      const lastIndex = lastFilledIndex(newCode)
      const nextIndex = Math.min(lastIndex + 1, 5)
      inputRefs.current[nextIndex]?.focus()
    }
  }, [])

  return (
    <div className="w-full">
      <div className="flex justify-between gap-4.5">
        {code.map((digit, index) => (
          <MagicAuthField
            key={FIELD_KEYS[index]}
            ref={(el) => {
              inputRefs.current[index] = el
            }}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={onFocus}
            disabled={disabled || isVerifying}
            hasError={Boolean(error)}
            aria-label={`Digit ${index + 1} of verification code`}
          />
        ))}
      </div>

      {error && <ErrorText error={error} className="mt-2 text-center w-full" />}

      <Button
        label={isVerifying ? 'Verifying...' : 'Verify'}
        onClick={() => handleVerification(code.join(''))}
        disabled={disabled || isVerifying || code.join('').length !== 6}
        isLoading={isVerifying}
        type="button"
        className="w-full mt-9"
        variant={ButtonVariant.Primary}
      />

      <Text
        variant="md"
        className={cn(
          'mt-6',
          'text-(--color-text-tertiary) dark:text-(--color-text-secondary-contrast)',
          'text-center w-full',
        )}
      >
        We’ve sent a 6-digit code to your email.
      </Text>
    </div>
  )
}
